import { Directive, ElementRef, Input, AfterViewInit, OnDestroy, HostListener } from '@angular/core';

@Directive({
  selector: '[dialogTableAccessibility]'
})
export class DialogTableAccessibilityDirective implements AfterViewInit, OnDestroy {
  @Input() ariaLabel: string = '';
  private observer: MutationObserver;
  private isUpdating = false;
  private currentFocusIndex = 0;

  constructor(private el: ElementRef) {
    this.observer = new MutationObserver(() => {
      if (!this.isUpdating) {
        this.updateAccessibility();
      }
    });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const rows = this.getSelectableRows();
    if (!rows.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1, rows);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1, rows);
        break;
      case 'Home':
        event.preventDefault();
        this.setFocus(0, rows);
        break;
      case 'End':
        event.preventDefault();
        this.setFocus(rows.length - 1, rows);
        break;
    }
  }

  private moveFocus(delta: number, rows: HTMLElement[]) {
    let newIndex = this.currentFocusIndex + delta;
    if (newIndex < 0) newIndex = rows.length - 1;
    if (newIndex >= rows.length) newIndex = 0;
    this.setFocus(newIndex, rows);
  }

  private setFocus(index: number, rows: HTMLElement[]) {
    // Remove tabindex from all rows except the current one
    rows.forEach((row, i) => {
      row.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    this.currentFocusIndex = index;
    rows[index].focus();
  }

  private getSelectableRows(): HTMLElement[] {
    const tbody = this.el.nativeElement.querySelector('tbody');
    return tbody ? Array.from(tbody.querySelectorAll('tr[role="option"]')) : [];
  }

  ngAfterViewInit() {
    this.updateAccessibility();
    
    // Start observing only after initial setup
    this.observer.observe(this.el.nativeElement, {
      childList: true,
      subtree: true
    });

    // Set initial tabindex states
    setTimeout(() => {
      const rows = this.getSelectableRows();
      if (rows.length) {
        rows.forEach((row, index) => {
          row.setAttribute('tabindex', index === 0 ? '0' : '-1');
        });
      }
    });
  }

  ngOnDestroy() {
    this.observer.disconnect();
  }

  private updateAccessibility() {
    this.isUpdating = true;

    try {
      const table = this.el.nativeElement.querySelector('table');
      if (table) {
        if (table.getAttribute('role') !== 'listbox') {
          table.setAttribute('role', 'listbox');
        }
        if (table.getAttribute('aria-multiselectable') !== 'true') {
          table.setAttribute('aria-multiselectable', 'true');
        }
        if (table.getAttribute('aria-label') !== this.ariaLabel) {
          table.setAttribute('aria-label', this.ariaLabel);
        }

        const thead = table.querySelector('thead');
        const tbody = table.querySelector('tbody');
        if (thead && thead.getAttribute('role') !== 'none') {
          thead.setAttribute('role', 'none');
        }
        if (tbody && tbody.getAttribute('role') !== 'none') {
          tbody.setAttribute('role', 'none');
        }

        // Update tabindex for rows
        const rows = this.getSelectableRows();
        if (rows.length) {
          rows.forEach((row, index) => {
            row.setAttribute('tabindex', index === this.currentFocusIndex ? '0' : '-1');
          });
        }
      }
    } finally {
      this.isUpdating = false;
    }
  }
}